import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../stores/auth.js'
import { api, mediaUrl } from '../services/api.js'
import { usePageTitle } from '../hooks/usePageTitle.js'
import { ResearcherProfileBlock } from './ResearcherProfilePage.jsx'
import ConnectPayoutsPanel from '../components/mysteries/ConnectPayoutsPanel.jsx'
import { purchasesAllowed, isNativeApp } from '../utils/platform.js'
import MobileMenu from '../components/shared/MobileMenu.jsx'
import DeleteAccountModal from '../components/shared/DeleteAccountModal.jsx'

const TIER_LABELS = {
  free: 'Free',
  plus: 'Plus',
  researcher: 'Researcher',
  family: 'Researcher',
  archive: 'Researcher',
}

export default function ProfilePage() {
  usePageTitle('Profile')
  const { user, login, token } = useAuthStore()
  const [deletingAccount, setDeletingAccount] = useState(false)
  const [nameForm, setNameForm]   = useState({ name: user?.name || '' })
  const [pwForm, setPwForm]       = useState({ currentPassword: '', newPassword: '', confirm: '' })
  const [nameMsg, setNameMsg]     = useState(null)
  const [pwMsg, setPwMsg]         = useState(null)
  const [saving, setSaving]       = useState(null)

  useEffect(() => {
    setNameForm({ name: user?.name || '' })
  }, [user?.name])

  async function saveName(e) {
    e.preventDefault()
    if (!nameForm.name.trim()) return
    setSaving('name')
    setNameMsg(null)
    try {
      const updated = await api.patch('/auth/me', { name: nameForm.name.trim() })
      login(token, { ...user, ...updated })
      setNameMsg({ type: 'ok', text: 'Name updated.' })
    } catch (err) {
      setNameMsg({ type: 'err', text: err.message })
    } finally {
      setSaving(null)
    }
  }

  async function savePassword(e) {
    e.preventDefault()
    if (pwForm.newPassword !== pwForm.confirm) {
      setPwMsg({ type: 'err', text: 'Passwords do not match.' })
      return
    }
    setSaving('pw')
    setPwMsg(null)
    try {
      await api.patch('/auth/me', {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      })
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' })
      setPwMsg({ type: 'ok', text: 'Password updated.' })
    } catch (err) {
      setPwMsg({ type: 'err', text: err.message })
    } finally {
      setSaving(null)
    }
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header__top">
          <div>
            <div className="page-header__label">Account</div>
            <h1 className="page-header__title">Profile</h1>
          </div>
        </div>
      </div>

      <div className="profile-page">

        <div className="profile-card">
          <div className="profile-card__header">
            <div className="profile-avatar">{user?.name?.[0]?.toUpperCase() || '?'}</div>
            <div style={{ flex: 1 }}>
              <div className="profile-card__name">{user?.name}</div>
              <div className="profile-card__email">{user?.email}</div>
              <span className="profile-tier">{TIER_LABELS[user?.tier] || 'Free'}</span>
            </div>
            <Link
              to="/billing"
              className="btn btn-ghost btn-sm"
              style={{ alignSelf: 'flex-start' }}
            >
              {/* No "Upgrade" CTA on native (Apple 3.1.1) — lands on the
                  web-managed plan view instead. */}
              {!purchasesAllowed()
                ? 'View plan'
                : (user?.tier && user.tier !== 'free' ? 'Manage subscription' : 'Upgrade')}
            </Link>
          </div>
        </div>

        {/* Native app: the Profile screen is the account/settings hub, so the
            secondary nav (otherwise in the hidden sidebar) lives here. */}
        {isNativeApp() && <MobileMenu />}

        <div className="profile-section">
          <h2 className="profile-section__title">Display name</h2>
          <form onSubmit={saveName} className="profile-form">
            <input
              className="form-input"
              type="text"
              value={nameForm.name}
              onChange={e => setNameForm({ name: e.target.value })}
              placeholder="Your name"
              required
            />
            {nameMsg && (
              <p className={nameMsg.type === 'ok' ? 'form-success' : 'error'}>{nameMsg.text}</p>
            )}
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving === 'name'}>
              {saving === 'name' ? 'Saving...' : 'Save name'}
            </button>
          </form>
        </div>

        <SelfPersonPicker user={user} onSaved={refreshed => login(token, { ...user, ...refreshed })} />

        <ResearcherPanel />

        {(user?.tier === 'researcher' || user?.tier === 'family' || user?.tier === 'archive') && <FamilyArchivePanel />}

        <div className="profile-section">
          <h2 className="profile-section__title">Change password</h2>
          <form onSubmit={savePassword} className="profile-form">
            <label>
              Current password
              <input
                className="form-input"
                type="password"
                value={pwForm.currentPassword}
                onChange={e => setPwForm({ ...pwForm, currentPassword: e.target.value })}
                placeholder="••••••••"
                required
              />
            </label>
            <label>
              New password
              <input
                className="form-input"
                type="password"
                value={pwForm.newPassword}
                onChange={e => setPwForm({ ...pwForm, newPassword: e.target.value })}
                placeholder="Min. 8 characters"
                required
                minLength={8}
              />
            </label>
            <label>
              Confirm new password
              <input
                className="form-input"
                type="password"
                value={pwForm.confirm}
                onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })}
                placeholder="Repeat new password"
                required
              />
            </label>
            {pwMsg && (
              <p className={pwMsg.type === 'ok' ? 'form-success' : 'error'}>{pwMsg.text}</p>
            )}
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving === 'pw'}>
              {saving === 'pw' ? 'Saving...' : 'Update password'}
            </button>
          </form>
        </div>

        {/* Account deletion — required in-app for the App Store (Apple 5.1.1),
            and good practice on web too. */}
        <div className="profile-section">
          <h2 className="profile-section__title">Delete account</h2>
          <p className="profile-section__hint" style={{ fontSize: '0.86rem', color: 'var(--text-muted)', marginBottom: '0.9rem', lineHeight: 1.55 }}>
            Permanently delete your account and remove your personal data. Past
            contributions stay in the shared archive but are no longer linked to
            you. This cannot be undone.
          </p>
          <button className="btn btn-danger btn-sm" onClick={() => setDeletingAccount(true)}>
            Delete my account
          </button>
        </div>

      </div>

      {deletingAccount && <DeleteAccountModal onClose={() => setDeletingAccount(false)} />}
    </div>
  )
}

// Researcher panel — shows the user's own XP/level + public-profile
// opt-in toggle. Re-uses the same ResearcherProfileBlock the public
// /researcher/<slug> page renders so the two views stay in sync.
function ResearcherPanel() {
  const [profile, setProfile] = useState(null)
  const [saving, setSaving]   = useState(false)

  useEffect(() => {
    api.get('/researcher/me').then(setProfile).catch(() => {})
  }, [])

  async function togglePublic(enabled) {
    setSaving(true)
    try {
      const r = await api.patch('/researcher/me', { public_profile_enabled: enabled })
      setProfile(p => p && ({ ...p, public_profile_enabled: r.public_profile_enabled, slug: r.public_profile_slug || p.slug }))
    } finally {
      setSaving(false)
    }
  }

  if (!profile) return null

  const memberSince = profile.member_since
    ? new Date(profile.member_since).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : null

  return (
    <div className="profile-section">
      <h2 className="profile-section__title">Researcher</h2>
      <ResearcherProfileBlock profile={profile} memberSince={memberSince} variant="own" />

      <div className="researcher-profile__toggle">
        <label>
          <input
            type="checkbox"
            checked={!!profile.public_profile_enabled}
            disabled={saving}
            onChange={e => togglePublic(e.target.checked)}
          />
          <span>Show my profile publicly at <code>/researcher/{profile.slug || '…'}</code></span>
        </label>
        {profile.public_profile_enabled && profile.slug && (
          <Link to={`/researcher/${profile.slug}`} className="researcher-profile__view-link">
            View my public profile →
          </Link>
        )}
      </div>

      <ConnectPayoutsPanel />
    </div>
  )
}

// Family Archive Export — Family-tier users can generate a designed PDF
// book of their entire tree, rate-limited to once per tree per 30 days.
// Generation runs async; this panel polls for status + offers download
// once ready.
function FamilyArchivePanel() {
  const [trees, setTrees] = useState([])
  const [treeId, setTreeId] = useState('')
  const [exports, setExports] = useState([])
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState(null)

  useEffect(() => {
    api.get('/trees').then(ts => { setTrees(ts || []); if (ts?.[0]) setTreeId(ts[0].id) }).catch(() => {})
    refreshExports()
  }, [])

  async function refreshExports() {
    try { setExports(await api.get('/family/archive-exports') || []) } catch {}
  }

  async function startExport() {
    if (!treeId) return
    setBusy(true); setMsg(null)
    try {
      await api.post('/family/archive-export', { treeId })
      setMsg({ type: 'ok', text: 'Export queued — your archive will appear below when ready (usually under a minute).' })
      // Poll once after a short delay, then again after a few more.
      setTimeout(refreshExports, 2000)
      setTimeout(refreshExports, 8000)
      setTimeout(refreshExports, 20000)
    } catch (err) {
      setMsg({ type: 'err', text: err.message || 'Failed to queue export' })
    } finally {
      setBusy(false)
    }
  }

  async function download(id) {
    try {
      const { url } = await api.get(`/family/archive-export/${id}/download`)
      window.open(url, '_blank')
    } catch (err) {
      setMsg({ type: 'err', text: err.message })
    }
  }

  return (
    <div className="profile-section">
      <h2 className="profile-section__title">Family archive export</h2>
      <p className="profile-section__hint" style={{ fontSize: '0.86rem', color: 'var(--text-muted)', marginBottom: '0.9rem', lineHeight: 1.55 }}>
        Generate a designed PDF book of your whole tree — every person, dates, places and notes. One export per tree per 30 days.
      </p>
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.8rem' }}>
        <select className="form-input" value={treeId} onChange={e => setTreeId(e.target.value)} style={{ flex: 1, minWidth: 200 }}>
          {trees.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" onClick={startExport} disabled={!treeId || busy}>
          {busy ? 'Queueing…' : 'Export archive'}
        </button>
      </div>
      {msg && <p className={msg.type === 'ok' ? 'form-success' : 'error'}>{msg.text}</p>}
      {exports.length > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginTop: '0.8rem' }}>
          {exports.map(x => (
            <div key={x.id} style={{
              padding: '0.6rem 0.75rem', borderBottom: '1px solid var(--border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.6rem',
            }}>
              <div>
                <div style={{ fontWeight: 500 }}>{x.tree_name || 'Tree'}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  {new Date(x.created_at).toLocaleDateString()} · {x.status}
                  {x.page_count ? ` · ${x.page_count} pages` : ''}
                </div>
              </div>
              {x.status === 'ready'
                ? <button className="btn btn-primary btn-sm" onClick={() => download(x.id)}>Download</button>
                : <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                    {x.status === 'generating' ? 'Generating…' : x.status === 'queued' ? 'In queue' : x.status === 'failed' ? 'Failed' : ''}
                  </span>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// "This is me" — link the user to the person in their own tree that
// represents them. When set, the Dashboard + sidebar avatar use that
// person's portrait photo instead of an initial.
function SelfPersonPicker({ user, onSaved }) {
  const [trees, setTrees] = useState([])
  const [persons, setPersons] = useState([])
  const [treeId, setTreeId] = useState('')
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  useEffect(() => {
    api.get('/trees').then(ts => {
      setTrees(ts || [])
      if (ts && ts[0]) setTreeId(ts[0].id)
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (!treeId) return
    api.get(`/persons?tree_id=${treeId}`).then(setPersons).catch(() => setPersons([]))
  }, [treeId])

  async function save(personId) {
    setSaving(true); setMsg(null)
    try {
      await api.patch('/auth/me/self-person', { person_id: personId })
      const me = await api.get('/auth/me')
      onSaved?.(me)
      setMsg({ type: 'ok', text: personId ? 'Linked.' : 'Unlinked.' })
    } catch (err) {
      setMsg({ type: 'err', text: err.message || 'Failed' })
    } finally {
      setSaving(false)
    }
  }

  const filtered = persons
    .filter(p => {
      if (!search) return true
      const n = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase()
      return n.includes(search.toLowerCase())
    })
    .slice(0, 15)

  const linked = persons.find(p => p.id === user?.self_person_id)

  return (
    <div className="profile-section">
      <h2 className="profile-section__title">This is me</h2>
      <p className="profile-section__hint" style={{ fontSize: '0.86rem', color: 'var(--text-muted)', marginBottom: '0.9rem', lineHeight: 1.55 }}>
        Link yourself to the person in your tree that represents you.
        Your dashboard avatar will use that person's portrait photo.
      </p>

      {linked && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.65rem',
          padding: '0.5rem 0.75rem', border: '1px solid var(--border)',
          borderRadius: '8px', background: 'color-mix(in srgb, var(--green) 5%, white)',
          marginBottom: '0.8rem',
        }}>
          <span style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'var(--green)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', flexShrink: 0,
          }}>
            {user?.self_photo_thumb_key
              ? <img src={mediaUrl(user.self_photo_thumb_key)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : (linked.first_name?.[0]?.toUpperCase() || '?')}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500 }}>{linked.first_name} {linked.last_name}</div>
            <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>Currently linked</div>
          </div>
          <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => save(null)}>Unlink</button>
        </div>
      )}

      {trees.length > 1 && (
        <select
          className="form-input"
          value={treeId}
          onChange={e => setTreeId(e.target.value)}
          style={{ marginBottom: '0.6rem' }}
        >
          {trees.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      )}

      <input
        type="text"
        className="form-input"
        placeholder="Search persons in this tree…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: '0.6rem' }}
      />
      <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center' }}>No matches</div>
        ) : filtered.map(p => (
          <button
            key={p.id}
            className="btn btn-ghost btn-sm"
            disabled={saving || p.id === user?.self_person_id}
            onClick={() => save(p.id)}
            style={{
              display: 'flex', width: '100%', padding: '0.55rem 0.75rem',
              textAlign: 'left', borderRadius: 0, borderBottom: '1px solid var(--border)',
              justifyContent: 'space-between', alignItems: 'center',
              // btn-ghost defaults to a muted text colour for the
              // secondary-action look, which makes this long picker
              // list feel washed out. Force the primary text colour on
              // the name so it reads clearly against the cream bg.
              color: 'var(--text)',
            }}
          >
            <span>
              <strong style={{ color: 'var(--text)' }}>{p.first_name} {p.last_name}</strong>
              {p.birth_date && <span style={{ marginLeft: '0.5rem', color: 'var(--text-muted)', fontSize: '0.78rem' }}>b. {p.birth_date}</span>}
            </span>
            {p.id === user?.self_person_id
              ? <span style={{ color: 'var(--green)', fontSize: '0.78rem' }}>Linked</span>
              : <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>Pick →</span>
            }
          </button>
        ))}
      </div>
      {msg && <p className={msg.type === 'ok' ? 'form-success' : 'error'} style={{ marginTop: '0.6rem' }}>{msg.text}</p>}
    </div>
  )
}
